if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        this.showActiveSlide();
        if (!this.elements.thumbnails) return;

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      }

      // The slides keep the product's own media order (capra-product.liquid),
      // so the selected variant's photo is not always the first slide. Scroll
      // the track to it once the track has a width: at once on the product
      // page, and after the quick-add modal opens, since quick-add.js inserts
      // this section while the modal is still hidden. The observer runs after
      // layout and before paint, so the first slide never shows. The viewer is
      // read with querySelector because, inside the modal, this constructor
      // runs before the nested slider-component has upgraded.
      showActiveSlide() {
        const track = this.elements.viewer.querySelector('[id^="Slider-"]');
        const activeMedia = track?.querySelector('[data-media-id].is-active');
        if (!activeMedia || activeMedia === track.firstElementChild) return;
        const observer = new ResizeObserver((entries) => {
          if (!entries[0].contentRect.width) return;
          observer.disconnect();
          track.scrollTo({ left: activeMedia.offsetLeft, behavior: 'instant' });
          const thumbnail = this.elements.thumbnails?.querySelector(`[data-target="${activeMedia.dataset.mediaId}"]`);
          this.setActiveThumbnail(thumbnail, 'instant');
        });
        observer.observe(track);
      }

      onSlideChanged(event) {
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }
        // product-info.js calls this on every variant change, including a size
        // pick whose variant carries the same photo as the one already showing.
        // The thumbnail and live-region work below only applies when the photo
        // actually changes, so note that before the classes are rewritten.
        const mediaChanged = !activeMedia.classList.contains('is-active');
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.firstChild !== activeThumbnail && activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        if (!mediaChanged) return;

        // Dawn scrolled the window up to the new photo here whenever its top
        // sat above the viewport, so a colour pick made further down the page
        // jumped back to the gallery. Picking a variant must not move the page
        // (user decision, September 13, 2026): only the viewer's own track
        // scrolls, to bring the new slide into the frame.
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      // behavior 'auto' follows the list's CSS scroll-behavior (smooth); the
      // load-time call passes 'instant'.
      setActiveThumbnail(thumbnail, behavior = 'auto') {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);

        // Capra stacks the thumbnails in a column beside the panel from 990px
        // (section-capra-product.css), so the list scrolls vertically there
        // and sideways below that width.
        const slider = this.elements.thumbnails.slider;
        if (window.getComputedStyle(slider).flexDirection === 'column') {
          const top = thumbnail.offsetTop;
          if (top >= slider.scrollTop && top + thumbnail.clientHeight <= slider.scrollTop + slider.clientHeight) return;
          slider.scrollTo({ top, behavior });
          return;
        }

        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        slider.scrollTo({ left: thumbnail.offsetLeft, behavior });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
